// src/components/PortalVerificacao/SecaoVideos.tsx
// Seção de vídeos AWS — exibida após confirmação de pagamento
// URLs via variáveis de ambiente (VITE_VIDEO_*)
// CREATED: 2026-05-07 — v1.0

import React, { useState } from 'react';
import { Play, CheckCircle, Lock } from 'lucide-react';
import { VerificacaoData } from '../../services/verificacaoService';

const BRAND = { gold: '#D4AF37', goldDeep: '#B88917', charcoal: '#1F2328' };

// ⚠️ Altere estas constantes ou configure as variáveis de ambiente
const VIDEO_BOAS_VINDAS = import.meta.env.VITE_VIDEO_BOAS_VINDAS_URL
  || 'https://seu-bucket.s3.sa-east-1.amazonaws.com/videos/boas-vindas.mp4';

const VIDEO_INSTRUCOES  = import.meta.env.VITE_VIDEO_INSTRUCOES_URL
  || 'https://seu-bucket.s3.sa-east-1.amazonaws.com/videos/instrucoes-portal.mp4';

const VIDEOS = [
  {
    src:       VIDEO_BOAS_VINDAS,
    title:     'Boas-Vindas à Federação',
    subtitle:  'Conheça a federação e todos os seus benefícios como filiado',
    duration:  '~5 min',
    icon:      '👋',
  },
  {
    src:       VIDEO_INSTRUCOES,
    title:     'Como Usar o Portal do Filiado',
    subtitle:  'Guia completo para navegar e aproveitar seu portal de acesso',
    duration:  '~8 min',
    icon:      '🎓',
  },
];

interface Props { data: VerificacaoData; }

const SecaoVideos: React.FC<Props> = ({ data }) => {
  const [assistidos, setAssistidos] = useState<Set<number>>(new Set());

  const marcarAssistido = (i: number) => {
    setAssistidos(prev => new Set([...prev, i]));
  };

  // Bloqueado se vídeos não liberados
 if (data.status !== 'active') {

    return (
      <div className="rounded-3xl border bg-white p-6 shadow-sm opacity-60" style={{ borderColor: `${BRAND.gold}33` }}>
        <div className="flex items-center gap-3">
          <Lock className="w-6 h-6 text-gray-400" />
          <div>
            <h3 className="text-base font-black text-gray-400">Vídeos de Boas-Vindas</h3>
            <p className="text-sm text-gray-400">Disponível após a confirmação do pagamento</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-3xl border bg-white p-6 shadow-sm" style={{ borderColor: `${BRAND.gold}33` }}>
      {/* Cabeçalho */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-2xl" style={{ backgroundColor: `${BRAND.gold}1A` }}>
          <Play className="w-6 h-6" style={{ color: BRAND.goldDeep }} />
        </div>
        <div>
          <h3 className="text-lg font-black" style={{ color: BRAND.charcoal }}>Vídeos de Boas-Vindas</h3>
          <p className="text-sm text-gray-500">Assista antes de acessar o portal pela primeira vez</p>
        </div>
        {assistidos.size === VIDEOS.length && (
          <div className="ml-auto flex items-center gap-1 text-xs font-bold text-green-600 bg-green-50 px-3 py-1.5 rounded-full">
            <CheckCircle className="w-3.5 h-3.5" /> Todos assistidos
          </div>
        )}
      </div>

      {/* Grid de vídeos */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {VIDEOS.map((v, i) => (
          <div
            key={i}
            className="rounded-2xl overflow-hidden border transition"
            style={{
              borderColor: assistidos.has(i) ? '#bbf7d055' : `${BRAND.gold}33`,
              backgroundColor: assistidos.has(i) ? '#f0fdf4' : 'white',
            }}
          >
            {/* Player */}
            <div className="relative bg-black">
              <video
                controls
                className="w-full"
                preload="metadata"
                onEnded={() => marcarAssistido(i)}
                style={{ maxHeight: '220px' }}
              >
                <source src={v.src} type="video/mp4" />
                <p className="text-white text-sm p-4">Seu navegador não suporta vídeos HTML5.</p>
              </video>
              {assistidos.has(i) && (
                <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1">
                  <CheckCircle className="w-4 h-4" />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="p-4">
              <div className="flex items-start gap-2">
                <span className="text-xl">{v.icon}</span>
                <div className="flex-1">
                  <p className="text-sm font-black" style={{ color: BRAND.charcoal }}>{v.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{v.subtitle}</p>
                  <p className="text-xs text-gray-400 mt-1.5 font-medium">{v.duration}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-gray-400 text-center mt-5">
        💡 Os vídeos ficam disponíveis permanentemente nesta página para consulta futura.
      </p>
    </div>
  );
};

export default SecaoVideos;